/**
 * 作品存储（DataProxy — V12 数据中心）
 *
 * 存储两类作品：
 *   1. MyProject：用户在编辑器创建的代码项目
 *   2. ForgeProject：完成铸剑阁关卡后自动生成的教学成就
 *
 * 数据结构设计支持后续在线部署（CloudBase / MongoDB）
 */

import { dataProxy } from "../core/data-proxy.js";

// ============================================================
// 类型定义
// ============================================================

export type WorkSource = "editor" | "forge";
export type BattleType = "1v1" | "2v2" | "3v3";
export type ForgeState = "completed" | "current" | "locked";
export type ForgeScore = "S" | "A" | "B" | "C";

/** 我的作品（编辑器创建的项目） */
export interface MyProject {
  source: "editor";
  id: string;
  name: string;
  code: string;
  createdAt: number;
  updatedAt: number;
  type: BattleType;
  tags?: string[];
  description?: string;
  /** 项目来源（user=玩家创建, example=从示例复制） */
  projectType?: "user" | "example";
  /** 示例项目的源 ID */
  exampleId?: string;
}

/** 铸剑阁作品（关卡成就） */
export interface ForgeProject {
  source: "forge";
  id: string;
  chapterNum: number;
  levelId: string;
  levelName: string;
  state: ForgeState;
  score?: ForgeScore;
  timeUsed?: number;
  difficulty: number;
  completedAt?: number;
}

export type Work = MyProject | ForgeProject;

// ============================================================
// 存储操作
// ============================================================

/** 获取所有作品 */
export function getAllWorks(): { myProjects: MyProject[]; forgeProjects: ForgeProject[] } {
  return dataProxy.get<{ myProjects: MyProject[]; forgeProjects: ForgeProject[] }>("works")
    ?? { myProjects: [], forgeProjects: [] };
}

/** 保存所有作品 */
function saveAllWorks(data: { myProjects: MyProject[]; forgeProjects: ForgeProject[] }): void {
  dataProxy.set("works", data);
}

// ============================================================
// 我的作品操作
// ============================================================

/** 添加我的作品 */
export function addMyProject(project: Omit<MyProject, "source" | "id" | "createdAt" | "updatedAt">): MyProject {
  const now = Date.now();
  const id = `proj-${now}-${Math.random().toString(36).slice(2, 9)}`;
  const myProject: MyProject = {
    source: "editor",
    id,
    createdAt: now,
    updatedAt: now,
    ...project,
  };
  const data = getAllWorks();
  data.myProjects.push(myProject);
  saveAllWorks(data);
  return myProject;
}

/** 更新我的作品 */
export function updateMyProject(id: string, updates: Partial<Omit<MyProject, "source" | "id" | "createdAt">>): MyProject | null {
  const data = getAllWorks();
  const idx = data.myProjects.findIndex((p) => p.id === id);
  if (idx === -1) return null;
  const updated: MyProject = {
    ...data.myProjects[idx],
    ...updates,
    updatedAt: Date.now(),
  };
  data.myProjects[idx] = updated;
  saveAllWorks(data);
  return updated;
}

/** 删除我的作品 */
export function deleteMyProject(id: string): boolean {
  const data = getAllWorks();
  const idx = data.myProjects.findIndex((p) => p.id === id);
  if (idx === -1) return false;
  data.myProjects.splice(idx, 1);
  saveAllWorks(data);
  return true;
}

/** 获取单个我的作品 */
export function getMyProject(id: string): MyProject | null {
  const data = getAllWorks();
  return data.myProjects.find((p) => p.id === id) ?? null;
}

/** 获取所有我的作品 */
export function listMyProjects(): MyProject[] {
  const data = getAllWorks();
  return data.myProjects;
}

// ============================================================
// 铸剑阁作品操作
// ============================================================

/** 添加铸剑阁作品 */
export function addForgeProject(project: Omit<ForgeProject, "source" | "id">): ForgeProject {
  const id = `forge-${project.chapterNum}-${project.levelId}`;
  const forgeProject: ForgeProject = {
    source: "forge",
    id,
    ...project,
  };
  const data = getAllWorks();
  // 检查是否已存在（避免重复）
  const existing = data.forgeProjects.find((p) => p.id === id);
  if (existing) {
    // 更新现有记录
    Object.assign(existing, forgeProject);
  } else {
    data.forgeProjects.push(forgeProject);
  }
  saveAllWorks(data);
  return forgeProject;
}

/** 更新铸剑阁作品 */
export function updateForgeProject(id: string, updates: Partial<Omit<ForgeProject, "source" | "id">>): ForgeProject | null {
  const data = getAllWorks();
  const idx = data.forgeProjects.findIndex((p) => p.id === id);
  if (idx === -1) return null;
  const updated: ForgeProject = {
    ...data.forgeProjects[idx],
    ...updates,
  };
  data.forgeProjects[idx] = updated;
  saveAllWorks(data);
  return updated;
}

/** 获取单个铸剑阁作品 */
export function getForgeProject(id: string): ForgeProject | null {
  const data = getAllWorks();
  return data.forgeProjects.find((p) => p.id === id) ?? null;
}

/** 获取所有铸剑阁作品 */
export function listForgeProjects(): ForgeProject[] {
  const data = getAllWorks();
  return data.forgeProjects;
}

// ============================================================
// 聚合查询
// ============================================================

/** 获取所有作品（合并） */
export function listAllWorks(): Work[] {
  const data = getAllWorks();
  return [...data.myProjects, ...data.forgeProjects];
}

/** 按来源筛选 */
export function filterBySource(source: WorkSource): Work[] {
  const data = getAllWorks();
  if (source === "editor") return data.myProjects;
  if (source === "forge") return data.forgeProjects;
  return [];
}

/** 按类型筛选（仅我的作品） */
export function filterByType(type: BattleType): MyProject[] {
  const data = getAllWorks();
  return data.myProjects.filter((p) => p.type === type);
}

/** 按来源和类型筛选 */
export function filterBySourceAndType(source: WorkSource, type?: BattleType): Work[] {
  const data = getAllWorks();
  if (source === "editor") {
    return type ? data.myProjects.filter((p) => p.type === type) : data.myProjects;
  }
  if (source === "forge") {
    return data.forgeProjects;
  }
  return [];
}

/** 按更新时间排序（最新优先） */
export function sortByLatest(works: Work[]): Work[] {
  return [...works].sort((a, b) => {
    const timeA = "updatedAt" in a ? a.updatedAt : a.completedAt ?? 0;
    const timeB = "updatedAt" in b ? b.updatedAt : b.completedAt ?? 0;
    return timeB - timeA;
  });
}

/** 清空所有作品（仅用于测试/重置） */
export function clearAllWorks(): void {
  dataProxy.remove("works");
}

// ============================================================
// V9: 数据验证（Task 7）
// ============================================================

/**
 * 验证 ForgeProject 数据完整性
 *
 * @param project - 要验证的项目
 * @returns 验证结果 { valid: boolean, errors: string[] }
 *
 * 验证项：
 * - chapterNum > 0
 * - levelId 非空
 * - state ∈ {completed, current, locked}
 * - score ∈ {S, A, B, C}（如果存在）
 * - difficulty ∈ [1, 5]
 * - completedAt > 0（如果存在）
 */
export function validateForgeProject(project: ForgeProject): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // 检查 chapterNum
  if (!Number.isInteger(project.chapterNum) || project.chapterNum <= 0) {
    errors.push(`chapterNum 必须是正整数，当前值: ${project.chapterNum}`);
  }

  // 检查 levelId
  if (!project.levelId || typeof project.levelId !== "string") {
    errors.push(`levelId 必须是非空字符串，当前值: ${project.levelId}`);
  }

  // 检查 state
  const validStates: ForgeState[] = ["completed", "current", "locked"];
  if (!validStates.includes(project.state)) {
    errors.push(`state 必须是 ${validStates.join("/")}，当前值: ${project.state}`);
  }

  // 检查 score（如果存在）
  if (project.score !== undefined) {
    const validScores: ForgeScore[] = ["S", "A", "B", "C"];
    if (!validScores.includes(project.score)) {
      errors.push(`score 必须是 ${validScores.join("/")}，当前值: ${project.score}`);
    }
  }

  // 检查 difficulty
  if (!Number.isInteger(project.difficulty) || project.difficulty < 1 || project.difficulty > 5) {
    errors.push(`difficulty 必须是 1-5 的整数，当前值: ${project.difficulty}`);
  }

  // 检查 completedAt（如果存在）
  if (project.completedAt !== undefined && (!Number.isInteger(project.completedAt) || project.completedAt <= 0)) {
    errors.push(`completedAt 必须是正整数时间戳，当前值: ${project.completedAt}`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * 验证所有 ForgeProject 数据
 *
 * @returns 验证结果 { valid: boolean, invalidProjects: Array<{ id: string, errors: string[] }> }
 */
export function validateAllForgeProjects(): {
  valid: boolean;
  invalidProjects: Array<{ id: string; errors: string[] }>;
} {
  const data = getAllWorks();
  const invalidProjects: Array<{ id: string; errors: string[] }> = [];

  for (const project of data.forgeProjects) {
    const result = validateForgeProject(project);
    if (!result.valid) {
      invalidProjects.push({
        id: project.id,
        errors: result.errors,
      });
    }
  }

  return {
    valid: invalidProjects.length === 0,
    invalidProjects,
  };
}

/**
 * 修复无效的 ForgeProject 数据
 *
 * @param project - 要修复的项目
 * @returns 修复后的项目
 *
 * 修复策略：
 * - chapterNum: 默认 1
 * - levelId: 默认 "unknown"
 * - state: 默认 "completed"
 * - score: 默认 "C"
 * - difficulty: 默认 1，范围限制 [1, 5]
 * - completedAt: 默认当前时间戳
 */
export function fixForgeProject(project: ForgeProject): ForgeProject {
  return {
    ...project,
    chapterNum: Number.isInteger(project.chapterNum) && project.chapterNum > 0 ? project.chapterNum : 1,
    levelId: project.levelId && typeof project.levelId === "string" ? project.levelId : "unknown",
    state: (["completed", "current", "locked"] as const).includes(project.state) ? project.state : "completed",
    score: (["S", "A", "B", "C"] as const).includes(project.score ?? "C") ? project.score : "C",
    difficulty: Math.max(1, Math.min(5, Number.isInteger(project.difficulty) ? project.difficulty : 1)),
    completedAt: Number.isInteger(project.completedAt ?? 0) && (project.completedAt ?? 0) > 0 ? project.completedAt : Date.now(),
  };
}
